import moment, {Moment} from 'moment-timezone';
import {TaskAlert, TaskAlertSchedule, TaskAlertScheduleDay} from '../../shared/services/api/task.interface';
import {ChartDataPoint} from 'canvasjs';
import {ChartsUtil, IsoWeekday} from '@app/layout/charts/charts-util';
import {WorkingHours} from '@app/graphql-typings/graphql';
import {SiteInfo} from '@app/shared/services/api/access-sites.interface';
import {DAY_SECONDS, HOUR_SECONDS, MINUTE_SECONDS} from '@app/shared/util/general/duration-utils';

interface Period {
  startTs: Moment;
  endTs: Moment;
}

/** Wall clock time of day, in the shape moment's `set()` accepts. */
export interface MomentUpdateTimeStruct {
  hour: number;
  minute: number;
  second: number;
}

/** Working hours window of a weekday, as wall clock time. */
export interface WorkHoursWeekdayBoundsConfig {
  start: MomentUpdateTimeStruct;
  end: MomentUpdateTimeStruct;
}

/** Working hours window of a weekday, as seconds from the start of the local day. */
export interface WorkingHourSecondsToMidnightBounds {
  start: number;
  end: number;
}

export class ScheduleUtil {
  static readonly WEEKDAY_NAMES = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
  static readonly ZERO_TIME = '00:00';
  static readonly TIME_FORMAT = 'HH:mm';

  static isSomeWorkingHoursEnabled(workingHours: WorkingHours): boolean {
    return workingHours ? Object.keys(workingHours).some(key => key.endsWith('Enabled') && workingHours[key]) : false;
  }

  static isAlwaysOnWorkingHours(workingHours: WorkingHours): boolean {
    if (!workingHours) {
      return false;
    }

    // Find first not 'fully-on' day config
    return !ScheduleUtil.WEEKDAY_NAMES.some(weekdayName => {
      return !workingHours[weekdayName + 'Enabled'] || workingHours[weekdayName + 'Start'] !== ScheduleUtil.ZERO_TIME
        || workingHours[weekdayName + 'End'] !== ScheduleUtil.ZERO_TIME;
    });
  }

  static isWorkingHoursAvailableOnChart(workingHours: WorkingHours | null): boolean {
    return workingHours && ScheduleUtil.isSomeWorkingHoursEnabled(workingHours) && !ScheduleUtil.isAlwaysOnWorkingHours(workingHours);
  }

  static canFilterByWorkingHours(site: SiteInfo | null): boolean {
    return site?.working_hours != null && ScheduleUtil.isSomeWorkingHoursEnabled(site.working_hours);
  }

  /** Whether a site local timestamp falls within that weekday's working hours. A weekday absent from `boundsByWeekday` is not a working day. */
  static isTimestampWithinWorkingHours(siteLocalTs: Moment, boundsByWeekday: Map<IsoWeekday, WorkingHourSecondsToMidnightBounds> | null): boolean {
    let isWithinWorkingHours = false;
    const bounds = boundsByWeekday?.get(siteLocalTs.isoWeekday());
    if (bounds) {
      const secondsToMidnight = (siteLocalTs.hour() * HOUR_SECONDS) + (siteLocalTs.minute() * MINUTE_SECONDS) + siteLocalTs.second();
      isWithinWorkingHours = secondsToMidnight >= bounds.start && secondsToMidnight < bounds.end;
    }
    return isWithinWorkingHours;
  }

  /** Working hours bounds of each enabled weekday as wall clock time. A weekday absent from the map is not a working day. */
  static buildWorkingHoursConfigByWeekday(workingHours: WorkingHours): Map<IsoWeekday, WorkHoursWeekdayBoundsConfig> {
    const configByWeekday = new Map<IsoWeekday, WorkHoursWeekdayBoundsConfig>();
    ScheduleUtil.WEEKDAY_NAMES.forEach((day, index) => {
      if (workingHours[`${day}Enabled`]) {
        const dayStart = workingHours[`${day}Start`];
        const dayEnd = workingHours[`${day}End`];
        const startTime = moment(dayStart, ScheduleUtil.TIME_FORMAT);
        const endTime = moment(dayEnd, ScheduleUtil.TIME_FORMAT);
        if (!startTime.isValid() || !endTime.isValid()) {
          throw new Error('Could not parse schedule for ' + day + '. Got: ' + dayStart + ' - ' + dayEnd);
        }
        configByWeekday.set(index + 1, {
          start: {hour: startTime.hour(), minute: startTime.minute(), second: startTime.second()},
          end: {hour: endTime.hour(), minute: endTime.minute(), second: endTime.second()}
        });
      }
    });
    return configByWeekday;
  }

  /** Convert working hours bounds to seconds from the start of the local day. An end of midnight means the end of the day. */
  static buildWorkingHoursSecondsToMidnightBoundsByWeekday(
    configByWeekday: Map<IsoWeekday, WorkHoursWeekdayBoundsConfig>
  ): Map<IsoWeekday, WorkingHourSecondsToMidnightBounds> {
    const boundsByWeekday = new Map<IsoWeekday, WorkingHourSecondsToMidnightBounds>();
    configByWeekday.forEach((config, weekday) => {
      const endSecondsToMidnight = ScheduleUtil.getSecondsToMidnight(config.end);
      boundsByWeekday.set(weekday, {
        start: ScheduleUtil.getSecondsToMidnight(config.start),
        end: endSecondsToMidnight || DAY_SECONDS
      });
    });
    return boundsByWeekday;
  }

  private static getSecondsToMidnight(time: MomentUpdateTimeStruct): number {
    return (time.hour * HOUR_SECONDS) + (time.minute * MINUTE_SECONDS) + time.second;
  }

  public static createChartDataForTaskAlert(taskAlert: TaskAlert, taskAlertSchedule: TaskAlertSchedule, chartStartDate: Moment, chartEndDate: Moment): ChartDataPoint[] {
    const chartStartDateInSiteTz = chartStartDate.clone().tz(taskAlertSchedule.timezone);
    const chartEndDateInSiteTz = chartEndDate.clone().tz(taskAlertSchedule.timezone);
    const alertStartTs = moment.max(moment.tz(taskAlert.start_ts, taskAlertSchedule.timezone), chartStartDateInSiteTz);
    const alertEndTs = taskAlert.end_ts
      ? moment.min(moment.tz(taskAlert.end_ts, taskAlertSchedule.timezone), chartEndDateInSiteTz)
      : moment.min(chartEndDateInSiteTz, moment.tz(taskAlertSchedule.timezone).add(1, 'ms'));
    const result: Period[] = this.getInSchedulePeriods(alertStartTs, alertEndTs, taskAlertSchedule);
    return this.buildChartDataPoints(result, chartStartDateInSiteTz, chartEndDateInSiteTz);
  }

  private static buildChartDataPoints(periods: Period[], chartStart: Moment, chartEnd: Moment): ChartDataPoint[] {
    const dataPoints: ChartDataPoint[] = [];
    periods?.forEach(period => {
      const s = period.startTs.clone().subtract(1, 'ms');
      if (s.isSameOrAfter(chartStart)) {
        dataPoints.push({x: s.toDate(), y: ChartsUtil.ALERT_VALUES.NO_ALERT});
      }

      dataPoints.push(
        {x: period.startTs.toDate(), y: ChartsUtil.ALERT_VALUES.ALERT},
        {x: period.endTs.toDate(), y: ChartsUtil.ALERT_VALUES.ALERT}
      );

      const e = period.endTs.clone().add(1, 'ms');
      if (e.isBefore(chartEnd)) {
        dataPoints.push({x: e.toDate(), y: ChartsUtil.ALERT_VALUES.NO_ALERT});
      }
    });

    return dataPoints;
  }

  private static getInSchedulePeriods(startTs: Moment, endTs: Moment, taskAlertSchedule: TaskAlertSchedule): Period[] {
    const result: Period[] = [];
    let previousPeriod: Period = null;
    const currentTs = startTs.clone().startOf('day');
    while (currentTs.isBefore(endTs)) {
      const periods = this.getInSchedulePeriodsForWeekday(currentTs, taskAlertSchedule);
      if (periods?.length) {
        if (previousPeriod != null) {
          while (periods.length) {
            const period = periods.shift();
            if (previousPeriod.endTs.isSame(period.startTs)) { // last period added ends when current period begins, merge (update last period's end to current period's end)
              previousPeriod.endTs = period.endTs;
            } else if (previousPeriod.endTs.isBefore(period.startTs)) { // last period and current is not connected
              result.push(period);
              previousPeriod = period;
            } else {
              throw new Error('Period\'s end ts ' + previousPeriod.endTs.format() + ' is after next period\'s start ts ' + period.startTs.format());
            }
          }
        } else {
          while (previousPeriod == null && periods.length) {
            const period = periods.shift();
            if (period.startTs.isSameOrAfter(startTs)) { // if period starts after alert starts, add whole period
              previousPeriod = periods[periods.length -1] ?? period;
              result.push(period);
            } else if (period.endTs.isAfter(startTs)) { // if period at least ends after alert starts, lop off excess start time
              const startAdjustedPeriod = {startTs: startTs, endTs: period.endTs};
              previousPeriod = periods[periods.length -1] ?? startAdjustedPeriod;
              result.push(startAdjustedPeriod);
            }
          }
        }
        result.push(...periods);
      }
      currentTs.add(1, 'day');
    }

    while (result.length) {
      const period = result[result.length - 1];
      if (period.startTs.isSameOrAfter(endTs)) { // period starts after alert ends, remove period
        result.pop();
      } else if (period.endTs.isAfter(endTs)) { // period ends after alert end, make period end = alert end
        period.endTs = endTs;
        break;
      } else {
        break;
      }
    }

    return result;
  }

  private static getInSchedulePeriodsForWeekday(ts: Moment, taskAlertSchedule: TaskAlertSchedule): Period[] {
    const periods: Period[] = [];
    const dowIndex = ts.isoWeekday() - 1;
    const scheduleDay: TaskAlertScheduleDay = taskAlertSchedule[this.WEEKDAY_NAMES[dowIndex]];
    if (scheduleDay.enabled) {
      const isStartTimeNonZero = scheduleDay.startTime !== this.ZERO_TIME;
      const isEndTimeNonZero = scheduleDay.endTime !== this.ZERO_TIME;
      const isSameNonZeroStartEnd = isStartTimeNonZero && isEndTimeNonZero && scheduleDay.startTime === scheduleDay.endTime;
      if (taskAlertSchedule.in_schedule) {
        if (!isSameNonZeroStartEnd) {
          const startTs = ts.clone().set(this.parseTime(scheduleDay.startTime));
          const endTs = ts.clone().set(this.parseTime(scheduleDay.endTime));
          if (scheduleDay.endTime === this.ZERO_TIME) {
            endTs.add(1, 'day');
          }
          periods.push({startTs, endTs});
        }
      } else {
        if (isSameNonZeroStartEnd) {
          const startTs = ts.clone().startOf('day');
          periods.push({startTs, endTs: startTs.clone().add(1, 'day')});
        } else {
          if (isStartTimeNonZero) {
            const startTs = ts.clone().set(this.parseTime(this.ZERO_TIME));
            const endTs = ts.clone().set(this.parseTime(scheduleDay.startTime));
            periods.push({startTs: startTs, endTs: endTs});
          }
          if (isEndTimeNonZero) {
            const startTs = ts.clone().set(this.parseTime(scheduleDay.endTime));
            const endTs = ts.clone().set(this.parseTime(this.ZERO_TIME)).add(1, 'day');
            periods.push({startTs: startTs, endTs: endTs});
          }
        }
      }
    }
    return periods;
  }

  private static parseTime(time: string): {hours: number, minutes: number, seconds: number, milliseconds: number} {
    const [hours, minutes] = time.split(':').map(Number);
    return {hours, minutes, seconds: 0, milliseconds: 0};
  }
}
